/* eslint-disable camelcase */
import { FastField, Field, Formik } from "formik"
import _ from "lodash"
import { observer } from "mobx-react-lite"
import { Spinner } from "native-base"
import * as React from "react"
import { Appearance, TextInput, ViewStyle } from "react-native"
import * as yup from "yup"
import { translate, TxKeyPath } from "../../../i18n"
import { PickerField } from "../../field/picker-field/picker-field"
import { FormControl } from "../../form-control/form-control"
import { PhoneInput } from "../../input/phone-input/phone-input"
import { FormProps } from "../form"

const FORM_DROPDOWN: ViewStyle = {
  marginBottom: 4,
  paddingHorizontal: 16
}

const ACTION_SHEET: ViewStyle = {
  height: 170,
  paddingHorizontal: 16,
}

const gender = [{id: 1, name: "man"}, {id: 2, name: "woman"},]

export interface EditProfileFormProps extends FormProps {
  /**
   * An optional style override useful for padding & margin.
   */
  initialValues?: {
    name?: string
    phone?: string
    email?: string
    gender_id?: number
    region_id?: number
    regions?: Array<any>
  }
}

const FieldInput = (
  { name, input = {}, nextInput = {}, colorScheme } :
  { name: string, input: any, nextInput: any, colorScheme?: "light" | "dark"}
) => (
  <Field name={name}>
    {({ field, form: { handleChange, handleBlur, handleSubmit, isValid }, meta }) => {
      return(
        <FormControl
          isRequired
          isInvalid={meta.touched && !!meta.error}
          labelTx={`editProfileForm.${_.camelCase(field.name)}` as TxKeyPath}
          labelPreset="title3"
          colorScheme={colorScheme}
          inputProps={{
            ref: input[field.name],
            testID: `jala:id/${field.name}-input`,
            autoCorrect: false,
            keyboardType: "default",
            returnKeyType: nextInput[field.name] ? "next" : "go",
            onSubmitEditing: () => nextInput[field.name] ? nextInput[field.name]() : (isValid && handleSubmit()),
            placeholderTx: `editProfileForm.${_.camelCase(field.name)}Placeholder` as TxKeyPath,
            defaultValue: field.value,
            onChangeText: handleChange(field.name),
            onBlur: handleBlur(field.name)
          }}
          errorMessage={meta.error}
          style={FORM_DROPDOWN}
        />
      )
      }
    }
  </Field>
)

/**
 * Describe your component here
 */
export const EditProfileForm = observer(function EditProfileForm(props: EditProfileFormProps) {
  const { initialValues = {}, onSubmit, colorScheme = "light" || Appearance.getColorScheme(), innerRef, ...rest } = props

  const input = {
    name: React.useRef<TextInput>(),
    email: React.useRef<TextInput>(),
    phone: React.useRef<TextInput>(),
    gender: React.useRef<TextInput>(),
  }

  const nextInput = {
    name: () => input.email.current && input.email.current.focus(),
    email: () => input.phone.current && input.phone.current.focus(),
    phone: () => input.gender.current && input.gender.current.focus(),
  }

  const validationSchema = yup.object({
    name: yup.string().ascii().required().label(translate("editProfileForm.name")),
    email: yup.string().email().required().label(translate("editProfileForm.email")),
    phone: yup.string().required().label(translate("editProfileForm.phone")),
    gender: yup.number().optional().label(translate("editProfileForm.gender")),
  })

  const loading = React.useMemo<boolean>(() => initialValues.regions.length === 0, [initialValues.regions.length])

  return (
    <Formik
      initialValues={{ ...initialValues }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      innerRef={innerRef}
      validateOnMount
      {...rest}
    >
      {({ initialValues, values, isValid, handleSubmit }) => (
        <>
          <FieldInput name="name" colorScheme={colorScheme} input={input} nextInput={nextInput} />
          <FastField name="email">
            {({ field, form: { handleChange, handleBlur }, meta }) => (
              <FormControl
                isRequired
                isInvalid={meta.touched && !!meta.error}
                labelTx={`editProfileForm.${_.camelCase(field.name)}` as TxKeyPath}
                labelPreset="title3"
                colorScheme={colorScheme}
                inputProps={{
                  testID: `jala:id/${field.name}-input`,
                  ref: input[field.name],
                  autoCapitalize: "none",
                  autoComplete: "email",
                  autoCorrect: false,
                  textContentType: "emailAddress",
                  keyboardType: "email-address",
                  returnKeyType: nextInput[field.name] ? "next" : "go",
                  onSubmitEditing: () => nextInput[field.name] ? nextInput[field.name]() : (isValid && handleSubmit()),
                  placeholderTx: `editProfileForm.${_.camelCase(field.name)}Placeholder` as TxKeyPath,
                  defaultValue: meta.initialValue,
                  onChangeText: handleChange(field.name),
                  onBlur: handleBlur(field.name)
                }}
                errorMessage={meta.error}
                style={FORM_DROPDOWN}
              />
            )}
          </FastField>
          <FastField name="phone">
            {({ field, form: { handleChange, handleBlur }, meta }) => (
              <FormControl
                isRequired
                isInvalid={meta.touched && !!meta.error}
                labelTx={`editProfileForm.${_.camelCase(field.name)}` as TxKeyPath}
                labelPreset="title3"
                colorScheme={colorScheme}
                errorMessage={meta.error}
                style={FORM_DROPDOWN}
              >
                <PhoneInput
                  colorScheme={colorScheme}
                  isInvalid={meta.touched && !!meta.error}
                  textProps={{
                    testID: `jala:id/${field.name}-input`,
                    ref: input[field.name],
                    autoCapitalize: "none",
                    autoComplete: "tel",
                    textContentType: "telephoneNumber",
                    returnKeyType: nextInput[field.name] ? "next" : "go",
                    onSubmitEditing: () => nextInput[field.name] ? nextInput[field.name]() : (isValid && handleSubmit()),
                    placeholderTx: `editProfileForm.${_.camelCase(field.name)}Placeholder` as TxKeyPath,
                    defaultValue: initialValues.phone,
                    onBlur: handleBlur(field.name),
                  }}
                  initialValue={initialValues.phone}
                  onChangePhoneNumber={handleChange(field.name)}
                />
              </FormControl>
            )}
          </FastField>
          <PickerField
            name="gender"
            txPrefix="editProfileForm"
            colorScheme={colorScheme}
            formControlProps={{ isRequired: true }}
            pickerProps={{
              data: gender,
              renderSelectedItemText: (item) => item && translate(`editProfileForm.${_.camelCase(item.name)}` as TxKeyPath),
              titleTx: "editProfileForm.genderPlaceholder",
              clearable: false,
              pickerActionsheetProps: {
                actionsheetStyle: ACTION_SHEET,
              }
            }}
          />
          <PickerField
            name="region_id"
            txPrefix="editProfileForm"
            colorScheme={colorScheme}
            formControlProps={{ isRequired: true, isDisabled: loading }}
            pickerProps={{
              data: values.regions,
              isDisabled: loading,
              InputRightElement: loading && <Spinner mr="9px"></Spinner>,
              renderSelectedItemText: (item) => item && translate(`editProfileForm.${_.camelCase(item.name)}` as TxKeyPath),
              titleTx: "editProfileForm.genderPlaceholder",
              isUseSearch: true,
              dataLabel: "full_name",
              fetchData: async (search) => {
                const result = values.regions.filter((region) => {
                  const name = region.full_name.toUpperCase() || '';
                  return name.includes(search.toUpperCase());
                })
                return result
              },
            }}
          />
        </>
      )}
    </Formik>
  )
})

EditProfileForm.displayName = 'EditProfileForm'
